import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import { isProfilePhotoOfMedia, StorageBuckets } from '@kit/shared/storage';

import { deletePublicCopies, publicR2Keys } from './dancer-media-visibility';
import type { DancerMediaContext } from './dancer-media.context';

/**
 * Delete media from storage and database
 */
export async function deleteMedia(
  ctx: DancerMediaContext,
  mediaId: string,
  userId: string,
): Promise<Result<void>> {
  try {
    // Fetch media details first
    const { data: media, error: fetchError } = await ctx.client
      .from('artist_media')
      .select('*')
      .eq('id', mediaId)
      .eq('user_id', userId)
      .single();

    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        return Result.fail(ServiceError.notFound('Media not found'));
      }
      return Result.fail(ServiceError.database(fetchError.message, fetchError));
    }

    // Delete the private original first, and keep the row if that fails. A
    // row deleted over a surviving file leaves an object nothing references,
    // which the public-profile folder rule can still serve. Storage reports a
    // removal its policy refused as success with nothing removed, so an
    // empty result is checked against the object itself.
    if (media.storage_path) {
      const storagePath = media.storage_path;
      const bucket = ctx.client.storage.from(StorageBuckets.DANCER_MEDIA);
      const { data: removed, error: storageError } = await bucket.remove([
        storagePath,
      ]);

      let stillThere = false;
      if (!storageError && (removed?.length ?? 0) === 0) {
        const { data: info, error: infoError } = await bucket.info(storagePath);
        stillThere = !infoError && !!info;
      }

      if (storageError || stillThere) {
        ctx.log('error', 'Failed to delete file from storage; row kept', {
          mediaId,
          storagePath,
          error: storageError?.message ?? 'removal refused',
        });
        return Result.fail(
          ServiceError.internal('Failed to delete the file from storage'),
        );
      }
    }

    // Delete from database
    const { error: deleteError } = await ctx.client
      .from('artist_media')
      .delete()
      .eq('id', mediaId);

    if (deleteError) {
      return Result.fail(
        ServiceError.database(deleteError.message, deleteError),
      );
    }

    // The public R2 copies outlive the row unless removed here. Best-effort:
    // the row is already gone, so a failure is reported, not returned.
    const r2Keys = publicR2Keys(media, () => ctx.getR2Service());
    if (r2Keys.length > 0) {
      const cleanup = await deletePublicCopies(ctx.getR2Service(), r2Keys);
      if (cleanup.isFailure) {
        ctx.log('warn', 'Failed to delete public R2 copies', {
          mediaId,
          keys: r2Keys,
          error: cleanup.getError().message,
        });
        captureServiceError(cleanup.getError(), {
          action: 'deleteMedia',
          step: 'r2_cleanup',
        });
      }
    }

    // A deleted headshot clears the profile photo only when it IS the
    // profile photo (`isProfilePhotoOfMedia`: its stored path, an absolute
    // URL to it, or the link of a link-only headshot). A photo set from
    // elsewhere stays. The update matches the value read, so a photo
    // changed in between is not cleared.
    if (media.media_type === 'headshot') {
      const { data: profile } = await ctx.client
        .from('profiles')
        .select('profile_photo_url')
        .eq('id', userId)
        .maybeSingle();
      const photoUrl = profile?.profile_photo_url;
      if (photoUrl && isProfilePhotoOfMedia(photoUrl, media)) {
        await ctx.client
          .from('profiles')
          .update({
            has_completed_media_step: false,
            profile_photo_url: null,
          })
          .eq('id', userId)
          .eq('profile_photo_url', photoUrl);
      }
    }

    return Result.ok(undefined);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'deleteMedia',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}
